// booking/utils/bookingNormalizer.js
import { fromCents } from './money'
export function normalizeBookingForForm(data) {
    if (!data) return null;

    return {
        rentalOrder: {
            status: data.rentalOrder?.status ?? 'UNPAID',
            startDate: data.rentalOrder?.startDate?.slice(0, 10) ?? '',
            endDate: data.rentalOrder?.endDate?.slice(0, 10) ?? '',
            startTime: data.rentalOrder?.startTime ?? '10:00',
            endTime: data.rentalOrder?.endTime ?? '10:00',
            supplierId: data.rentalOrder?.supplierId ?? '',
            referenceNumber: data.rentalOrder?.referenceNumber ?? '',
        },

        customerSnapshot: data.customerSnapshot ?? {
            name: '',
            lastName: '',
            email: '',
            prefix: '',
            phone: '',
            country: { id: null, name: '' },
            license: '',
            comment: '',
            language: 'en',
        },

        pricingSnapshot: data.pricingSnapshot ?? {
            calculation: {
                calculatedAt: '',
                customerCurrencyCode: '',
                supplierCurrencyCode: '',
                exchangeRate: 1,
                exchangeRateEur: 1,
                days: 1,
                extrasTotalWithFeesCents: 0,
                motorcycleTotalWithFeesCents: 0,
                discountTotalCents: 0,
                totalPriceCents: 0,
                finalPriceCents: 0,
                prepaymentAmountCents: 0,
                paidAmountCents: 0,
            },
        },

        invoiceSnapshot: data.invoiceSnapshot ?? {
            calculation: {
                commissionCents: 0,
                managementFeeCents: 0,
                rmbDiscountCents: 0,
                supplierDiscountCents: 0,
            },
        },

        shopSnapshot: data.shopSnapshot ?? {
            pickup: {
                shop: {
                    id: null,
                    name: '',
                    address: '',
                    prefix: '',
                    phone: '',
                    email: '',
                    paymentMethod: null,
                    depositMethod: null,
                    language: 'en',
                    policy: '',
                    shopType: '',
                    distanceUnit: 'KM',
                    durationType: 'DAILY',
                },
                location: {
                    id: null,
                    name: '',
                    city: { id: null, name: '' },
                    country: { id: null, name: '' },
                },
            },
            dropoff: null,
        },

        motorcycleSnapshot: data.motorcycleSnapshot ?? {
            shopMotorcycle: {
                id: '',
                motorcycleYear: '',
                motorcycleModel: {
                    id: '',
                    name: '',
                    brand: '',
                    type: '',
                    photoUrl: '',
                    modelYear: '',
                },
            },
        },

        rentalPolicySnapshot: data.rentalPolicySnapshot ?? {
            minAge: '',
            minExp: '',
            countryRestriction: false,
            canLeaveState: true,
            canLeaveCity: true,
            isUnlimitedMileage: false,
            mileageLimit: '',
            excessMileageFee: '',
            deposit: '',
            damageExcess: '',
            theftExcess: '',
            licenseType: '',
            cancellation7DaysRefund: '',
            cancellation15DaysRefund: '',
            cancellation30DaysRefund: '',
            cancellation60DaysRefund: '',
        },

        rentalOrderExtra: data.rentalOrderExtra ?? [],

        rentalOrderInsurance: (data.rentalOrderInsurance ?? []).map((i) => ({
            insuranceId: i.insuranceId,
            priceCents: i.priceCents,
            costCents: i.costCents,
            priceCentsEuro: i.priceCentsEuro ?? null,
        })),

        emailSettings: {
            sendToCustomer: false,
            sendToSupplier: false,
        },
    };
}

export function canShowCustomerContact(status) {
    return ['CONFIRMED', 'COMPLETED', 'INVOICED'].includes(status);
}

export function normalizeSupplierBookingDetail(data) {
    const booking = data?.rentalOrder || data || {};

    const currencyCode =
        booking?.Currency?.code ||
        data?.pricingSnapshot?.calculation?.customerCurrencyCode ||
        'EUR';

    const customerSnapshot =
        data?.customerSnapshot ||
        booking?.RentalOrderHistory?.[0]?.changes?.patch?.customerSnapshot ||
        {};

    const motorcycleSnapshot =
        data?.motorcycleSnapshot ||
        booking?.RentalOrderHistory?.[0]?.changes?.patch?.motorcycleSnapshot ||
        {};

    const shopSnapshot =
        data?.shopSnapshot ||
        booking?.RentalOrderHistory?.[0]?.changes?.patch?.shopSnapshot ||
        {};

    const pricingSnapshot =
        data?.pricingSnapshot ||
        booking?.RentalOrderHistory?.[0]?.changes?.patch?.pricingSnapshot ||
        {};

    const rentalPolicySnapshot =
        data?.rentalPolicySnapshot ||
        booking?.RentalOrderHistory?.[0]?.changes?.patch?.rentalPolicySnapshot ||
        {};

    const calculation = pricingSnapshot?.calculation || {};

    const extras =
        data?.rentalOrderExtra ||
        booking?.RentalOrderExtra ||
        booking?.extrasSnapshot?.extras ||
        booking?.extrasSnapshot ||
        [];

    const rawPaidAmountCents = Number(calculation?.paidAmountCents ?? 0);
    const insurancesTotalCents = Number(calculation?.insurancesTotalCents ?? 0);

    // Paid Amount 需要减去 insurance，但不能小于 0
    const adjustedPaidAmountCents = Math.max(
        rawPaidAmountCents - insurancesTotalCents,
        0
    );

    const motorcyclePriceCents = Number(
        calculation?.motorcycleTotalWithFeesCents ?? 0
    );

    const extraTotalCents = Number(
        calculation?.extrasTotalWithFeesCents ??
        calculation?.extrasTotalCents ??
        0
    );

    const discountTotalCents = Number(calculation?.discountTotalCents ?? 0);

    const pvpTotalCents = Number(
        calculation?.finalPriceCents ??
        calculation?.totalPriceCents ??
        booking?.totalPriceCents ??
        0
    );

    const pendingAmountCents = Math.max(pvpTotalCents - adjustedPaidAmountCents, 0);

    return {
        id: booking?.id,
        referenceNumber: booking?.referenceNumber,
        status: booking?.status,

        startDate: booking?.startDate,
        endDate: booking?.endDate,
        startTime: booking?.startTime,
        endTime: booking?.endTime,

        shopName: booking?.Shop?.name || shopSnapshot?.pickup?.shop?.name || '-',
        shopAddress:
            booking?.Shop?.address || shopSnapshot?.pickup?.shop?.address || '-',

        pickupLocation:
            booking?.Location?.name || shopSnapshot?.pickup?.location?.name || '-',

        pickupCity:
            booking?.Location?.City?.name ||
            shopSnapshot?.pickup?.location?.city?.name ||
            '-',

        pickupCountry:
            booking?.Location?.City?.Country?.name ||
            shopSnapshot?.pickup?.location?.country?.ISOCode ||
            '-',

        motorcycleBrand:
            motorcycleSnapshot?.shopMotorcycle?.motorcycleModel?.brand || '-',
        motorcycleName:
            motorcycleSnapshot?.shopMotorcycle?.motorcycleModel?.name || '-',
        motorcycleType:
            motorcycleSnapshot?.shopMotorcycle?.motorcycleModel?.type || '-',
        motorcycleYear:
            motorcycleSnapshot?.shopMotorcycle?.motorcycleYear ||
            booking?.ShopMotorcycleInfo?.motorcycleYear ||
            '-',
        motorcyclePhoto:
            motorcycleSnapshot?.shopMotorcycle?.motorcycleModel?.photoUrl || '',

        customerName:
            customerSnapshot?.name ||
            `${customerSnapshot?.firstName || ''} ${customerSnapshot?.lastName || ''}`.trim() ||
            '-',
        customerEmail: customerSnapshot?.email || booking?.guestEmail || '-',
        customerPhone: customerSnapshot?.phone || '-',
        customerPhonePrefix: customerSnapshot?.prefix || '',
        customerLanguage: customerSnapshot?.language || '-',
        customerLicense: customerSnapshot?.license || '-',

        currencyCode,

        motorcyclePriceCents,
        extraTotalCents,
        discountTotalCents,
        pvpTotalCents,
        paidAmountCents: adjustedPaidAmountCents,
        pendingAmountCents,

        extras: Array.isArray(extras) ? extras : [],

        deposit: rentalPolicySnapshot?.deposit ?? '',
        theftExcess: rentalPolicySnapshot?.theftExcess ?? '',
        damageExcess: rentalPolicySnapshot?.damageExcess ?? '',
        mileageLimit: rentalPolicySnapshot?.mileageLimit ?? '',
        excessMileageFee: rentalPolicySnapshot?.excessMileageFee ?? '',
        isUnlimitedMileage: Boolean(rentalPolicySnapshot?.isUnlimitedMileage),

        cancellation7DaysRefund: rentalPolicySnapshot?.cancellation7DaysRefund ?? 0,
        cancellation15DaysRefund: rentalPolicySnapshot?.cancellation15DaysRefund ?? 0,
        cancellation30DaysRefund: rentalPolicySnapshot?.cancellation30DaysRefund ?? 0,
        cancellation60DaysRefund: rentalPolicySnapshot?.cancellation60DaysRefund ?? 0,

        shopMotorcycles: data?.meta?.options?.shopMotorcycles || [],

        showCustomerContact: canShowCustomerContact(booking?.status),

        motorcyclePriceLabel: fromCents(motorcyclePriceCents, currencyCode),
        extraTotalLabel: fromCents(extraTotalCents, currencyCode),
        discountTotalLabel: fromCents(discountTotalCents, currencyCode),
        pvpTotalLabel: fromCents(pvpTotalCents, currencyCode),
        paidAmountLabel: fromCents(adjustedPaidAmountCents, currencyCode),
        pendingAmountLabel: fromCents(pendingAmountCents, currencyCode),
    };
}